import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Send, CheckCircle, AlertCircle, ArrowRight } from 'lucide-react';
import axios from 'axios';
import Seo from '../components/Seo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: '',
    subject: '', // Honeypot field
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      
      setSubmitStatus({
        type: 'success',
        message: response.data.message || 'Thank you for your message! We\'ll get back to you soon.',
      });

      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        message: '',
        subject: '',
      });
    } catch (error) {
      console.error('Contact form error:', error);
      setSubmitStatus({
        type: 'error',
        message: error.response?.data?.detail || 'Failed to submit form. Please try again or email us directly.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact-page" data-testid="contact-page">
      <Seo
        title="Contact DeltaOps AI | Request a Demo"
        description="Connect with DeltaOps AI to schedule a demo, explore partnerships, or discuss operational transformation."
        image="https://images.pexels.com/photos/19213884/pexels-photo-19213884.jpeg"
        imageAlt="Healthcare office workspace"
      />
      {/* Hero Section */}
      <section
        className="min-h-screen flex items-center bg-gradient-to-br from-slate-950 via-slate-900 to-cyan-900/20 text-white"
        data-testid="hero-section"
      >
        <div className="container mx-auto px-4 py-20 text-center">
          <div className="max-w-4xl mx-auto space-y-6">
            <p className="text-cyan-300 uppercase tracking-widest text-sm">Contact</p>
            <h1 className="text-4xl md:text-5xl font-bold leading-tight" data-testid="page-title">
              Contact DeltaOps AI
            </h1>
            <p className="text-lg text-slate-200">
              Ready to transform your hospital or home health operations with real-time coordination intelligence? Reach out and we&apos;ll walk you through the steps.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link
                to="/operations-flow"
                className="btn bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-6 py-3 rounded-lg transition hover:-translate-y-0.5"
              >
                Explore the Operations Flow <ArrowRight className="ml-2" />
              </Link>
              <a
                href="mailto:hi@deltaops.health"
                className="btn border border-white/20 px-6 py-3 rounded-lg text-white hover:text-cyan-300"
              >
                Email Our Team
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Info and Form */}
      <section className="py-20 bg-slate-900" data-testid="contact-section">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
            {/* Contact Information */}
            <div>
              <h2 className="text-3xl font-bold mb-6 text-white">Get in Touch</h2>
              <p className="text-lg text-slate-300 mb-8">
                We'd love to hear from you. Whether you have a question about features, pricing, implementation, or anything else, our team is ready to answer all your questions.
              </p>

              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="bg-white/5 p-3 rounded-lg border border-white/10">
                    <Mail className="w-6 h-6 text-cyan-300" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white mb-1">Email</h3>
                    <a
                      href="mailto:hi@deltaops.health"
                      className="text-cyan-300 hover:text-cyan-100"
                      data-testid="contact-email"
                    >
                      hi@deltaops.health
                    </a>
                  </div>
                </div>
              </div>

              {/* Optional: Add a visual element */}
              <div className="mt-12 rounded-2xl overflow-hidden shadow-lg border border-white/10">
                <img
                  src="https://images.pexels.com/photos/19213884/pexels-photo-19213884.jpeg"
                  alt="Office"
                  className="w-full h-64 object-cover"
                />
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-white/5 rounded-2xl shadow-2xl p-8 border border-white/10 backdrop-blur" data-testid="contact-form-container">
              <h2 className="text-2xl font-bold mb-6 text-white">Send us a Message</h2>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Honeypot field - hidden from users but visible to bots */}
                <div className="hidden" aria-hidden="true">
                  <label htmlFor="subject">Subject</label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    tabIndex="-1"
                    autoComplete="off"
                  />
                </div>

                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-slate-200 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    disabled={isSubmitting}
                    className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-cyan-400 focus:border-transparent disabled:bg-white/10 disabled:cursor-not-allowed"
                    placeholder="John Doe"
                    data-testid="contact-name-input"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-slate-200 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    disabled={isSubmitting}
                    className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-cyan-400 focus:border-transparent disabled:bg-white/10 disabled:cursor-not-allowed"
                    placeholder="john@hospital.com"
                    data-testid="contact-email-input"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-slate-200 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    disabled={isSubmitting}
                    className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-cyan-400 focus:border-transparent disabled:bg-white/10 disabled:cursor-not-allowed"
                    placeholder="+1 (555) 123-4567"
                    data-testid="contact-phone-input"
                  />
                </div>

                <div>
                  <label htmlFor="company" className="block text-sm font-semibold text-slate-200 mb-2">
                    Hospital/Organization
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    disabled={isSubmitting}
                    className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-cyan-400 focus:border-transparent disabled:bg-white/10 disabled:cursor-not-allowed"
                    placeholder="General Hospital"
                    data-testid="contact-company-input"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-slate-200 mb-2">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    disabled={isSubmitting}
                    rows="5"
                    className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-cyan-400 focus:border-transparent disabled:bg-white/10 disabled:cursor-not-allowed resize-none"
                    placeholder="Tell us about your needs..."
                    data-testid="contact-message-input"
                  ></textarea>
                </div>

                {submitStatus && (
                  <div
                    className={`p-4 rounded-lg flex items-start space-x-3 border ${
                      submitStatus.type === 'success'
                        ? 'bg-emerald-500/10 border-emerald-400/40'
                        : 'bg-rose-500/10 border-rose-400/40'
                    }`}
                    data-testid="contact-form-status"
                  >
                    {submitStatus.type === 'success' ? (
                      <CheckCircle className="w-6 h-6 text-emerald-300 flex-shrink-0 mt-0.5" />
                    ) : (
                      <AlertCircle className="w-6 h-6 text-rose-300 flex-shrink-0 mt-0.5" />
                    )}
                    <p
                      className={`${
                        submitStatus.type === 'success' ? 'text-emerald-100' : 'text-rose-100'
                      }`}
                    >
                      {submitStatus.message}
                    </p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full btn btn-primary py-4 text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="contact-submit-button"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Sending...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center">
                      Send Message <Send className="ml-2" size={20} />
                    </span>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-cyan-600/20 via-slate-900 to-slate-950 text-white" data-testid="cta-section">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Schedule a Personalized Demo</h2>
          <p className="text-xl mb-8 text-cyan-100 max-w-2xl mx-auto">
            See how DeltaOps AI can work for your specific needs. Our team will walk you through the platform and answer all your questions.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="mailto:info@deltaops.health"
              className="btn bg-white/10 text-white border border-white/30 hover:bg-white/20 text-lg px-8 py-4"
            >
              Email Us
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
